import { Cloud } from '@cast/shared-types';
import { Cluster, Region } from '@cast/types';
import { EdgeLocation, OmniCluster } from '@cast/types-omni';

import { CUSTOM_EDGE_PROVIDER } from './constants';
import {
  CastManagedEdgeLocationProvider,
  EdgeLocationMappedState,
  EdgeLocationProvider,
  EdgeLocationTemplateUsage,
} from './types';

import {
  isEKSCluster,
  isGKECluster,
} from '@cast/shared-cluster-context-data-access';

export const getEdgeLocationProvider = (
  edgeLocation: EdgeLocation
): EdgeLocationProvider => {
  if (edgeLocation.aws) {
    return Cloud.aws;
  }

  if (edgeLocation.gcp) {
    return Cloud.gcp;
  }

  if (edgeLocation.oci) {
    return Cloud.oracle;
  }

  if (edgeLocation.nebius) {
    return Cloud.nebius;
  }

  if (edgeLocation.custom) {
    return CUSTOM_EDGE_PROVIDER;
  }

  return Cloud.unknown;
};

export const clusterToProvider = (cluster: Cluster): Cloud | '' => {
  if (isEKSCluster(cluster)) {
    return Cloud.aws;
  } else if (isGKECluster(cluster)) {
    return Cloud.gcp;
  }
  return '';
};

export const getRegionDisplayName = (
  regionCode?: string,
  regions?: Region[]
): string => {
  if (!regionCode) {
    return '-';
  }

  if (!regions) {
    return regionCode;
  }

  const region = regions.find((r) => r.name === regionCode);
  return region?.displayName || regionCode;
};

export const getEdgeLocationMappedState = (
  edgeLocation: EdgeLocation,
  templateUsage: EdgeLocationTemplateUsage
): EdgeLocationMappedState => {
  switch (edgeLocation.state) {
    case 'INITIALIZING':
    case 'PENDING_ONBOARDING':
      return EdgeLocationMappedState.Pending;

    case 'READY':
      if (edgeLocation.custom) {
        return (edgeLocation.totalEdgeCount || 0) > 0
          ? EdgeLocationMappedState.InUse
          : EdgeLocationMappedState.Ready;
      }
      return templateUsage.totalEnabled > 0
        ? EdgeLocationMappedState.InUse
        : EdgeLocationMappedState.Ready;

    case 'OFFBOARDING':
      return EdgeLocationMappedState.Offboarding;

    case 'FAILED':
      return EdgeLocationMappedState.Failed;

    case 'DELETED':
      return EdgeLocationMappedState.Deleted;

    default:
      return EdgeLocationMappedState.Unknown;
  }
};

export const findOmniClusterInList = (
  clusterId: string,
  clusters?: OmniCluster[]
): OmniCluster | undefined => {
  return clusters?.find((c) => c.id === clusterId);
};

type EdgeLocationsLinkArgs = {
  clusterId: string;
};

export const makeEdgeLocationsLink = ({
  clusterId,
}: EdgeLocationsLinkArgs): string => {
  return `/automation/external-clusters/${clusterId}/autoscaler/settings/edge-locations`;
};

export const isCastManagedEdgeLocationProvider = (
  provider: EdgeLocationProvider
): provider is CastManagedEdgeLocationProvider => {
  switch (provider) {
    case 'custom':
      return false;
    default:
      return true;
  }
};
